/**
 * Lean Firestore fetchers for analytics.
 *
 * Each document is mapped to a minimal shape immediately after getDocs to
 * keep memory low and keep PII (email, phone, teamMembers, logos) out of
 * analytics state.  Firestore still bills one read per document.
 */

import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  REGISTRATION_COLLECTIONS,
  COMPETITION_META,
  TIMESTAMP_FIELD,
} from "./registrationsConfig";

/**
 * Resolve the timestamp field name for a given collection.
 */
function tsField(collectionName) {
  return TIMESTAMP_FIELD[collectionName] ?? TIMESTAMP_FIELD.default;
}

/**
 * Map a raw Firestore document to the lean analytics shape.
 * Returns null if both timestamp fields are missing (skip in caller).
 */
function toRecord(docSnap, collectionName) {
  const d = docSnap.data();
  const meta = COMPETITION_META[collectionName] ?? {};

  const rawTs = d.submittedAt ?? d.timestamp ?? null;
  const at = rawTs?.toDate?.() ?? null;

  // Resolve title from doc or fall back to meta
  const competitionTitle =
    d.competitionTitle ?? meta.title ?? collectionName;

  return {
    id: docSnap.id,
    collection: collectionName,
    competitionTitle,
    competitionSchool: d.competitionSchool ?? meta.competitionSchool ?? null,
    schoolName: d.schoolName ?? null,
    participationType: d.participationType ?? null,
    grade: d.grade ?? null,
    at,
  };
}

/**
 * Fetch lean analytics records from all registration collections in parallel.
 * Skips collections that fail (e.g. not yet created).
 *
 * @returns {{ records: object[], missingTimestamp: number }}
 */
export async function fetchLeanAnalytics() {
  const results = await Promise.allSettled(
    REGISTRATION_COLLECTIONS.map(async (name) => {
      const field = tsField(name);
      let snap;
      try {
        snap = await getDocs(
          query(collection(db, name), orderBy(field, "desc"))
        );
      } catch {
        // Index might be missing or collection doesn't exist — try unordered
        try {
          snap = await getDocs(collection(db, name));
        } catch {
          return [];
        }
      }
      return snap.docs.map((d) => toRecord(d, name));
    })
  );

  const records = [];
  let missingTimestamp = 0;

  for (const r of results) {
    if (r.status === "fulfilled") {
      for (const rec of r.value) {
        if (rec.at === null) {
          missingTimestamp++;
        }
        records.push(rec);
      }
    }
  }

  return { records, missingTimestamp };
}

/**
 * Fetch lean records for a single competition collection, optionally within a
 * date range.  Falls back to full collection fetch if the date-range query
 * fails (e.g. missing composite index).
 *
 * @param {string} collectionName
 * @param {{ from?: Date, to?: Date }} [range]
 * @returns {object[]}
 */
export async function fetchCompetitionLean(collectionName, range = {}) {
  const field = tsField(collectionName);
  const constraints = [orderBy(field, "desc")];

  if (range.from) {
    constraints.push(where(field, ">=", range.from));
  }
  if (range.to) {
    constraints.push(where(field, "<=", range.to));
  }

  let snap;
  try {
    snap = await getDocs(query(collection(db, collectionName), ...constraints));
  } catch {
    // Composite index missing or field absent — fall back to full fetch
    try {
      snap = await getDocs(collection(db, collectionName));
    } catch {
      return [];
    }
  }

  return snap.docs.map((d) => toRecord(d, collectionName));
}

/**
 * Get document counts for all collections using aggregated server queries
 * (1 read per collection instead of loading all docs).
 *
 * @returns {Promise<Record<string, number>>} map of collectionName → count
 */
export async function fetchCollectionCounts() {
  const results = await Promise.allSettled(
    REGISTRATION_COLLECTIONS.map(async (name) => {
      const snap = await getCountFromServer(collection(db, name));
      return { name, count: snap.data().count };
    })
  );

  const counts = {};
  for (const r of results) {
    if (r.status === "fulfilled") {
      counts[r.value.name] = r.value.count;
    }
  }
  return counts;
}
